import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import { fetchORSForSSR } from '@confluence/content-smartlinks/entry-points/fetchORSForSSR.ts';
import { getInitialState } from '@confluence/content-smartlinks/entry-points/getInitialState.ts';
import { getSmartShortcuts } from '@confluence/content-smartlinks/entry-points/getSmartShortcuts.ts';
import { timeGuard } from '@confluence/query-preloader-tools/entry-points/timeGuard.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient.ts';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations.ts';

import useShortcutsSpaceNavigationQuery, {
	type useShortcutsSpaceNavigationQuery$data,
	type useShortcutsSpaceNavigationQuery$variables,
} from './__generated__/useShortcutsSpaceNavigationQuery.graphql.ts';

declare global {
	interface Window {
		__SSR_SHORTCUTS__?: { [key: string]: any } | {};
	}
}

const SMART_LINK_MAX_TIME = 250;

export const preloadShortcutsSmartLinks = async (spaceKey: string) => {
	const { customURLs, userContext } = getSSRRenderInputs();
	// If we don't receive a URL - no reason to try and run queries
	const objectResolverURL = customURLs?.['object-resolver-service'];
	if (!objectResolverURL) {
		return;
	}

	// Call cc-graphql and get SmartLink URLs for the current space's shortcuts
	const res = await query<
		useShortcutsSpaceNavigationQuery$data,
		useShortcutsSpaceNavigationQuery$variables
	>({
		query: useShortcutsSpaceNavigationQuery,
		variables: {
			spaceKey,
		},
	});

	// Loop through query data and pull out smart links
	const smartLinks = getSmartShortcuts(res);

	// If there are no valid SmartLinks shortcuts - exit
	if (!smartLinks?.length) {
		return;
	}

	await SSRMeasures.run('ssr-app/prepare/preloadQuery/fetch:sidenavSmartLinksORS', async () => {
		const timeout = StatsigConfigurations.getNumber(
			'confluence_ssr_sidenav_smart_link_max_time',
			SMART_LINK_MAX_TIME,
		);

		const resolvedSmartLinks = await timeGuard(
			async () => {
				try {
					const res = await fetchORSForSSR({
						url: `${objectResolverURL}?timeout=${timeout}`,
						userContext,
						body: JSON.stringify(smartLinks),
					});
					if (!res.ok) {
						throw new Error(`${res.status} ${res.statusText}`);
					}
					return res.json();
				} catch (e) {
					ssrPrepareErrorsClient.submitError(
						new Error(`Smart link shortcuts ORS call failed: ${e.message}`),
					);
				}
				return [];
			},
			[],
			timeout,
		);

		if (resolvedSmartLinks.length) {
			// Format response to fit SmartCard Provider storeOptions initial state
			// Save correctly formatted response to window obj so it can be used on SSR and SPA (no duplicate calls)
			window['__SSR_SHORTCUTS__'] = getInitialState(resolvedSmartLinks);
		}
	});
};
